// Shared sort config for Budget Control mobile (Card + Graph views)

export interface BudgetSortOption {
  label: string;
  value: string;
}

// Sort options mapped to the real budget API keys (mirrors the card metrics)
export const BUDGET_SORT_OPTIONS: BudgetSortOption[] = [
  { label: "Brand Name", value: "brand_display_name" },
  { label: "% QFTD Budget Used", value: "perc_of_qftd_budget_used" },
  { label: "% Budget Used", value: "perc_of_budget_used" },
  { label: "% of RR Budget Used", value: "perc_of_qftd_RR_budget_use" },
  { label: "QFTD", value: "qftd" },
  { label: "QFTD Budget", value: "qftd_budget" },
  { label: "Remaining QFTD", value: "remaining_qftd" },
  { label: "CPA Income", value: "cpa_income" },
  { label: "Total Income", value: "total_income" },
  { label: "CPA Budget", value: "cpa_constraint" },
  { label: "FTD", value: "ftd" },
];

// Graph view supports sorting by every budget metric (same set as the card view)
export const BUDGET_GRAPH_ENABLED_SORT_KEYS = new Set(
  BUDGET_SORT_OPTIONS.map((option) => option.value),
);

// Default sort (mirrors the trigger label shown in the filter)
export const DEFAULT_BUDGET_SORT: { key: string; dir: "asc" | "desc" } = {
  key: "brand_display_name",
  dir: "desc", //according to the figma design, the default sort direction is descending
};

const cleanNumber = (value: any): number =>
  parseFloat(String(value ?? "0").replace(/[% ,]/g, "")) || 0;

// Sort rows by the given key/direction. Brand name sorts alphabetically,
// everything else numerically; null/undefined values always sink to the bottom.
export const sortBudgetRows = (
  rows: any[],
  key: string,
  dir: "asc" | "desc",
): any[] => {
  if (!key || !rows?.length) return rows ?? [];
  return [...rows].sort((a, b) => {
    const aRaw = a?.[key];
    const bRaw = b?.[key];
    if (aRaw == null && bRaw == null) return 0;
    if (aRaw == null) return 1;
    if (bRaw == null) return -1;
    if (key === "brand_display_name") {
      return dir === "asc"
        ? String(aRaw).localeCompare(String(bRaw))
        : String(bRaw).localeCompare(String(aRaw));
    }
    const aNum = cleanNumber(aRaw);
    const bNum = cleanNumber(bRaw);
    return dir === "asc" ? aNum - bNum : bNum - aNum;
  });
};
